import { useState, useEffect, useRef } from 'react';
import { useAuthStore } from '@/lib/stores/auth.store';
import { io, Socket } from 'socket.io-client';
import { streamChatService } from '@/lib/api/streamChat.service';
import { notificationApiService } from '@/lib/api/notification.service';
import { useSessionContext } from '@om/shared-auth';
import type { NotificationFeedItem } from '@om/shared-types';

interface PendingTask {
  id: string;
  title: string;
  context?: string;
  dueDate?: string;
  celebrationId?: number;
  eventId?: number;
}

interface RecentMessage {
  id: string;
  userId: string;
  userName: string;
  time: string;
  message: string;
  hasImage?: boolean;
  channelCid?: string;
  celebrationId?: number;
}

interface RecentActivityItem {
  id: string;
  type: string;
  title: string;
  description: string;
  icon: string;
}

interface RightPanelData {
  pendingTasks: PendingTask[];
  recentMessages: RecentMessage[];
  recentActivity: RecentActivityItem[];
  notifications: NotificationFeedItem[];
}

export const useRightPanelData = (celebrationId?: number) => {
  const sessionContext = useSessionContext();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const user = useAuthStore((state) => state.user);
  const socketRef = useRef<Socket | null>(null);
  const isSessionActive = !sessionContext.loading && sessionContext.doesSessionExist;
  const [data, setData] = useState<RightPanelData>({
    pendingTasks: [],
    recentMessages: [],
    recentActivity: [],
    notifications: []
  });

  useEffect(() => {
    if (!isSessionActive || !isAuthenticated || !user?.id) {
      if (socketRef.current) {
        socketRef.current.emit('leave-notifications', { userId: user?.id });
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      return;
    }

    // Initialize Socket.IO connection - all notifications come through WebSockets
    if (!socketRef.current) {
      const socketUrl = import.meta.env.VITE_API_DOMAIN || window.location.origin;

      socketRef.current = io(socketUrl, {
        auth: { userId: user.id },
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        reconnectionAttempts: 5,
        transports: ['polling', 'websocket'],
      });

      socketRef.current.on('connect', () => {
        socketRef.current?.emit('join-notifications', { userId: user.id });
      });

      // Listen for pending tasks notification from backend
      socketRef.current.on('notification:pending-tasks', (tasks: PendingTask[]) => {
        setData(prev => ({
          ...prev,
          pendingTasks: tasks || []
        }));
      });

      // Listen for recent activity notification from backend
      socketRef.current.on('notification:recent-activity', (activity: RecentActivityItem[]) => {
        setData(prev => ({
          ...prev,
          recentActivity: activity || []
        }));
      });

      // Combined notification event for bulk updates
      socketRef.current.on('notification:update', (notification: Partial<RightPanelData>) => {
        setData(prev => ({
          ...prev,
          ...notification
        }));
      });

      socketRef.current.on('notification:feed', (notifications: NotificationFeedItem[]) => {
        setData(prev => ({
          ...prev,
          notifications: notifications || []
        }));
      });

      socketRef.current.on('notification:new', (notification: NotificationFeedItem) => {
        setData(prev => ({
          ...prev,
          notifications: [notification, ...prev.notifications.filter((item) => item.id !== notification.id)].slice(0, 10)
        }));
      });
    }

    return () => {
      // Clean up Socket.IO connection when component unmounts
      if (socketRef.current) {
        socketRef.current.emit('leave-notifications', { userId: user.id });
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [isSessionActive, isAuthenticated, user?.id]);

  useEffect(() => {
    if (!isSessionActive || !isAuthenticated || !user?.id || !socketRef.current) {
      return;
    }

    socketRef.current.emit('join-notifications', { userId: user.id, celebrationId });
  }, [isSessionActive, isAuthenticated, user?.id, celebrationId]);

  useEffect(() => {
    let isMounted = true;
    let refreshInterval: ReturnType<typeof setInterval> | null = null;

    if (!isSessionActive || !isAuthenticated || !user?.id) {
      return undefined;
    }

    const loadNotifications = async () => {
      try {
        const notifications = await notificationApiService.list(10, celebrationId);
        if (!isMounted) {
          return;
        }

        setData(prev => ({
          ...prev,
          notifications,
        }));
      } catch (error) {
        console.error('Failed to load notifications feed:', error);
      }
    };

    loadNotifications();
    refreshInterval = setInterval(loadNotifications, 30000);

    return () => {
      isMounted = false;
      if (refreshInterval) {
        clearInterval(refreshInterval);
      }
    };
  }, [isSessionActive, isAuthenticated, user?.id, celebrationId]);

  useEffect(() => {
    let isMounted = true;
    let refreshInterval: ReturnType<typeof setInterval> | null = null;

    if (!isSessionActive || !isAuthenticated || !user?.id) {
      return undefined;
    }

    const loadRecentMessages = async () => {
      try {
        const messages = await streamChatService.getRecentMessages(5);
        if (!isMounted) return;
        setData(prev => ({
          ...prev,
          recentMessages: messages.map((msg) => ({
            id: msg.id,
            userId: msg.userId,
            userName: msg.userName,
            time: msg.time,
            message: msg.message,
            hasImage: msg.hasImage,
            channelCid: msg.channelCid,
          }))
        }));
      } catch (error) {
        console.error('Failed to load recent Stream Chat messages:', error);
      }
    };

    loadRecentMessages();
    refreshInterval = setInterval(loadRecentMessages, 30000);

    return () => {
      isMounted = false;
      if (refreshInterval) {
        clearInterval(refreshInterval);
      }
    };
  }, [isSessionActive, isAuthenticated, user?.id]);

  const markAllNotificationsRead = async () => {
    try {
      await notificationApiService.markRead(celebrationId);
      setData(prev => ({
        ...prev,
        notifications: prev.notifications.map((item) => ({
          ...item,
          readAt: item.readAt ?? new Date().toISOString(),
        })),
      }));
    } catch (error) {
      console.error('Failed to mark notifications as read:', error);
    }
  };

  const markNotificationRead = async (recipientId: number) => {
    try {
      const marked = await notificationApiService.markOneRead(recipientId);
      if (!marked) {
        return;
      }

      setData(prev => ({
        ...prev,
        notifications: prev.notifications.map((item) =>
          item.id === recipientId
            ? { ...item, readAt: item.readAt ?? new Date().toISOString() }
            : item,
        ),
      }));
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  return {
    data,
    markAllNotificationsRead,
    markNotificationRead,
  };
};
